/**
 * ExWorks | Trading Playground Manual App
 *
 * Simple hash-based routing for manual pages
 * No authentication required - public read-only access
 *
 * Language detection priority:
 * 1. URL query parameter: ?lang=en
 * 2. localStorage: manual_language
 * 3. Browser language (navigator.language)
 * 4. Default: ja
 */

import { useState, useEffect, useCallback } from 'react';
import ManualIndexPage from './pages/ManualIndexPage';
import ManualSectionPage from './pages/ManualSectionPage';
import ManualArticlePage from './pages/ManualArticlePage';
import ContactPage from './pages/ContactPage';
import AdminContactsPage from './pages/AdminContactsPage';
import { ErrorDisplay } from './components/ErrorDisplay';
import ContactModal from './components/ContactModal';
import './lib/supabase'; // Import to trigger validation at startup

type Route =
  | { page: 'index' }
  | { page: 'section'; sectionId: string }
  | { page: 'article'; slug: string }
  | { page: 'contact' }
  | { page: 'admin-contacts' }
  | { page: 'error'; error: Error };

/**
 * Redirect map for old/renamed URLs.
 * Maps old paths to their current correct paths (using /manual/article/ format).
 */
const REDIRECT_MAP: Record<string, string> = {
  '/manual/analytics/dashboard': '/manual/article/dashboard/dashboard-overview',
  '/manual/analytics/trades-list': '/manual/article/calendar/trades-list',
  '/manual/basics/broker-export': '/manual/article/import/mt4-mt5-export',
  '/manual/basics/csv-import': '/manual/article/import/csv-upload',
  '/manual/basics/dataset-switching': '/manual/article/settings/data-management',
  '/manual/basics/demo-trading': '/manual/article/getting-started/demo-overview',
  '/manual/basics/fees-spread': '/manual/article/reference/glossary',
  '/manual/basics/margin': '/manual/article/reference/glossary',
  '/manual/basics/swap': '/manual/article/reference/glossary',
  '/manual/basics/trade-list': '/manual/article/calendar/trades-list',
  '/manual/ai/ai-overview': '/manual/article/ai/ai-how-it-works',
  '/manual/ai/coach-selection': '/manual/article/ai/ai-coaching-details',
  '/manual/features/ai-proposals': '/manual/article/ai/ai-how-to-use',
  '/manual/features/monthly-review': '/manual/article/ai/monthly-review',
  '/manual/getting-started/import-first': '/manual/article/getting-started/how-to-use',
  '/manual/getting-started/quickstart': '/manual/article/getting-started/start-in-3-min',
  '/manual/import/import-history': '/manual/article/import/csv-upload',
  '/manual/import/duplicates-missing-inconsistency': '/manual/article/support/csv-not-importing',
  '/manual/dashboard/overview': '/manual/article/dashboard/dashboard-overview',
  '/manual/playbooks/avoid-kotsudokan': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/change-one-rule': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/find-edge': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/losing-streak-safety': '/manual/article/playbooks/case-study-dd-recovery',
  '/manual/playbooks/lot-management': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/optimize-exit': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/optimize-trade-frequency': '/manual/article/playbooks/case-study-overtrading',
  '/manual/playbooks/pair-selection': '/manual/article/playbooks/case-study-currency-pair',
  '/manual/playbooks/safe-money-management': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/stop-loss-basics': '/manual/article/reference/glossary',
  '/manual/playbooks/sustainable-rules': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/trading-mindset': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/trailing-stop': '/manual/article/reference/glossary',
  '/manual/playbooks/type-based-improvement': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/unrealized-pnl-management': '/manual/article/reference/glossary',
  '/manual/playbooks/verification-period': '/manual/article/playbooks/fix-order',
  '/manual/playbooks/weekly-review-15min': '/manual/article/playbooks/weekly-routine',
  '/manual/reports/balance-drawdown': '/manual/article/reports/balance-drawdown-over-20',
  '/manual/reports/market-currency-pair': '/manual/article/reports/reports-market',
  '/manual/reports/market-currency-pairs': '/manual/article/reports/reports-market',
  '/manual/reports/market-heatmap': '/manual/article/reports/reports-market',
  '/manual/reports/risk-r-multiple': '/manual/article/reports/reports-risk',
  '/manual/reports/strategy-setup': '/manual/article/reports/reports-strategy',
  '/manual/reports/strategy-setup-analysis': '/manual/article/reports/reports-strategy',
  '/manual/reports/strategy-setup-direction': '/manual/article/reports/reports-strategy',
  '/manual/reports/time-daily': '/manual/article/reports/reports-time',
  '/manual/reports/time-holding-time': '/manual/article/reports/time-holding-duration',
  '/manual/reports/time-monthly': '/manual/article/reports/reports-time',
  '/manual/reports/time-time-of-day': '/manual/article/reports/time-golden-hours',
  '/manual/reports/time-weekday': '/manual/article/reports/time-weekday-analysis',
  '/manual/reports/time-weekly': '/manual/article/reports/reports-time',
  '/manual/settings/dataset': '/manual/article/settings/data-management',
  '/manual/settings/display-language': '/manual/article/settings/profile',
  '/manual/settings/overview': '/manual/article/settings/profile',
  '/manual/settings/data-export-backup': '/manual/article/settings/data-management',
  '/manual/data/data-management-guide': '/manual/article/settings/data-management',
  '/manual/data/data-export-backup': '/manual/article/settings/data-management',
  '/manual/article/settings/data-management-guide': '/manual/article/settings/data-management',
  '/manual/article/settings/data-export-backup': '/manual/article/settings/data-management',
  '/manual/support/demo-restrictions': '/manual/article/getting-started/demo-overview',
  '/manual/support/faq-import': '/manual/article/support/faq-getting-started',
  '/manual/support/overview': '/manual/article/support/troubleshooting-guide',
  '/manual/support/troubleshooting': '/manual/article/support/troubleshooting-guide',
};

function detectLanguage(): 'ja' | 'en' {
  // 1. URL query parameter
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang');
  if (langParam === 'en' || langParam === 'ja') {
    localStorage.setItem('manual_language', langParam);
    return langParam;
  }

  // 2. localStorage
  const stored = localStorage.getItem('manual_language');
  if (stored === 'en' || stored === 'ja') {
    return stored;
  }

  // 3. Browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('en')) {
    return 'en';
  }

  // 4. Default
  return 'ja';
}

function parseRoute(hash: string): Route {
  try {
    // Remove leading #
    let path = hash.replace(/^#/, '');

    // Empty or /manual -> index
    if (!path || path === '/manual' || path === '/') {
      return { page: 'index' };
    }

    // Check redirect map for old/renamed URLs
    if (REDIRECT_MAP[path]) {
      const newPath = REDIRECT_MAP[path];
      window.location.hash = `#${newPath}`;
      path = newPath;
    }

    // /admin/contacts -> admin contacts management page
    if (path === '/admin/contacts') {
      return { page: 'admin-contacts' };
    }

    // /manual/article/support/contact -> contact page (special route)
    if (path === '/manual/article/support/contact') {
      return { page: 'contact' };
    }

    // /manual/article/section-id/article-slug -> article page
    // Slug format in DB: "reports/reports-overview", "playbooks/daily-routine"
    const articleMatch = path.match(/^\/manual\/article\/([a-z0-9-]+\/[a-z0-9-]+)$/);
    if (articleMatch) {
      return { page: 'article', slug: articleMatch[1] };
    }

    // /manual/section-id -> section page
    // Section IDs: "reports", "playbooks", "getting-started", etc.
    const sectionMatch = path.match(/^\/manual\/([a-z0-9-]+)$/);
    if (sectionMatch) {
      return { page: 'section', sectionId: sectionMatch[1] };
    }

    // Default to index
    return { page: 'index' };
  } catch (error) {
    console.error('Route parsing error:', error);
    return { page: 'index' };
  }
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  const [language, setLanguage] = useState<'ja' | 'en'>(() => detectLanguage());

  useEffect(() => {
    // Listen for hash changes
    const handleHashChange = () => {
      setRoute(parseRoute(window.location.hash));
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update document title when language changes
  useEffect(() => {
    document.title = language === 'en'
      ? 'ExWorks | Trading Playground - No-Code EA Builder'
      : 'ExWorks | Trading Playground - ノーコード自動売買ビルダー';
  }, [language]);

  const handleLanguageChange = useCallback((lang: 'ja' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('manual_language', lang);
    // Update URL query parameter and redirect to manual TOP
    // (Japanese and English articles have different slug structures,
    //  so we navigate to the index to avoid "not found" errors)
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    url.hash = '#/manual';
    window.history.replaceState({}, '', url.toString());
    setRoute({ page: 'index' });
  }, []);

  // Render current page
  let page: React.ReactNode;

  if (route.page === 'error') {
    page = <ErrorDisplay error={route.error} />;
  } else if (route.page === 'section') {
    page = <ManualSectionPage sectionId={route.sectionId} language={language} onLanguageChange={handleLanguageChange} />;
  } else if (route.page === 'contact') {
    page = <ContactPage language={language} onLanguageChange={handleLanguageChange} />;
  } else if (route.page === 'admin-contacts') {
    page = <AdminContactsPage />;
  } else if (route.page === 'article') {
    page = <ManualArticlePage slug={route.slug} language={language} onLanguageChange={handleLanguageChange} />;
  } else {
    page = <ManualIndexPage language={language} onLanguageChange={handleLanguageChange} />;
  }

  return (
    <>
      {page}
      <ContactModal />
    </>
  );
}
